import { useState, useEffect, useCallback } from 'react'
import {
  DollarSign, RefreshCw, TrendingUp, TrendingDown, Minus,
} from 'lucide-react'
import { clsx } from 'clsx'
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts'
import { api } from '../lib/api'

const tooltipStyle = { backgroundColor: '#18181c', border: '1px solid #26262c', borderRadius: 8, fontSize: 11 }

export default function PricingTrendsPage() {
  const [data, setData] = useState<any>(null)
  const [selectedCategory, setSelectedCategory] = useState('cloud_compute')
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const { data: d } = await api.get('/api/pricing/trends')
      setData(d)
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={20} className="animate-spin text-text-muted" />
      </div>
    )
  }

  const quarters: string[] = data.quarters || []
  const categories = data.categories || {}
  const cat = categories[selectedCategory]

  // Build chart data — one object per quarter with all item prices
  const chartData = quarters.map((q: string, i: number) => {
    const point: any = { quarter: q }
    if (cat) {
      for (const item of cat.items) {
        point[item.name] = item.prices[i]
      }
    }
    return point
  })

  // Calculate trends for each item
  const itemTrends = (cat?.items || []).map((item: any) => {
    const prices = item.prices.filter((p: number | null) => p != null) as number[]
    if (prices.length < 2) return { ...item, change: 0, pctChange: 0 }
    const first = prices[0]
    const last = prices[prices.length - 1]
    const change = last - first
    const pctChange = first > 0 ? (change / first) * 100 : 0
    return { ...item, change, pctChange, first, last, current: last }
  })

  const CATEGORY_ICONS: Record<string, string> = {
    cloud_compute: 'Cloud (4vCPU)',
    cloud_compute_large: 'Cloud (16vCPU)',
    cloud_kubernetes: 'Managed K8s',
    cloud_database: 'Managed DB',
    cloud_serverless: 'Serverless',
    saas_observability: 'Observability SaaS',
    saas_security: 'Security SaaS',
    cdn_bandwidth: 'CDN & Bandwidth',
    gpu_cloud: 'GPU Cloud',
    gpu_hardware: 'GPU Hardware',
    servers: 'Servers',
    laptops: 'Laptops',
    memory_storage: 'Memory & Storage',
    semiconductors: 'Semiconductors',
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <TrendingUp size={20} className="text-emerald" /> Long-Term Pricing Trends
          </h1>
          <p className="text-[10px] text-text-secondary">
            5-year quarterly pricing: cloud, GPU, servers, laptops, RAM, semiconductors (Q1 2021 → Q2 2026)
          </p>
        </div>
      </div>

      {/* Category tabs */}
      <div className="flex gap-1.5 flex-wrap">
        {Object.entries(CATEGORY_ICONS).map(([key, label]) => (
          <button key={key} type="button" onClick={() => setSelectedCategory(key)}
            className={clsx('badge text-[10px] cursor-pointer px-3 py-1.5',
              selectedCategory === key ? 'badge-blue' : 'bg-surface-card text-text-muted border border-border')}>
            {label}
          </button>
        ))}
      </div>

      {/* Main chart */}
      {cat && (
        <div className="card p-4">
          <h2 className="text-sm font-semibold text-text-primary mb-1">{cat.name}</h2>
          <p className="text-[10px] text-text-muted mb-4">{cat.unit} — 22 quarters of data</p>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#26262c" />
              <XAxis dataKey="quarter" tick={{ fill: '#94a3b8', fontSize: 9 }} interval={2} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 9 }}
                tickFormatter={(v: number) => v >= 10000 ? `$${(v / 1000).toFixed(0)}k` : v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v}`} />
              <Tooltip contentStyle={tooltipStyle}
                formatter={(v: any) => (typeof v === 'number' ? `$${v.toLocaleString()}` : 'N/A')}
                labelFormatter={(l: any) => `Quarter: ${l}`} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              {cat.items.map((item: any) => (
                <Line
                  key={item.name}
                  type="monotone"
                  dataKey={item.name}
                  stroke={item.color}
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  connectNulls={false}
                  name={`${item.name} (${item.provider})`}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Item trend cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {itemTrends.map((item: any) => (
          <div key={item.name} className="card p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
              <span className="text-[10px] font-semibold text-text-primary truncate">{item.name}</span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-lg font-bold text-text-primary">
                  ${item.current >= 10000 ? `${(item.current / 1000).toFixed(0)}k` : item.current >= 1000 ? `${(item.current / 1000).toFixed(1)}k` : item.current}
                </span>
                <span className="text-[9px] text-text-muted ml-1">{cat?.unit}</span>
              </div>
              <div className="text-right">
                {item.pctChange < -2 ? (
                  <div className="flex items-center gap-1 text-emerald">
                    <TrendingDown size={14} />
                    <span className="text-xs font-bold">{item.pctChange.toFixed(1)}%</span>
                  </div>
                ) : item.pctChange > 2 ? (
                  <div className="flex items-center gap-1 text-rose">
                    <TrendingUp size={14} />
                    <span className="text-xs font-bold">+{item.pctChange.toFixed(1)}%</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-text-muted">
                    <Minus size={14} />
                    <span className="text-xs font-bold">{item.pctChange > 0 ? '+' : ''}{item.pctChange.toFixed(1)}%</span>
                  </div>
                )}
                <span className="text-[9px] text-text-muted">5yr change</span>
              </div>
            </div>
            <div className="flex justify-between text-[9px] text-text-muted mt-1">
              <span>Q1'21: ${item.first?.toLocaleString() || 'N/A'}</span>
              <span>Now: ${item.current?.toLocaleString()}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Key insights */}
      <div className="card p-4">
        <h3 className="text-xs font-semibold text-text-primary mb-2">Key Trends</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[10px] text-text-secondary">
          <div className="flex gap-2"><TrendingDown size={12} className="text-emerald shrink-0 mt-0.5" /><span><b className="text-emerald">GPU prices dropping:</b> H100 down from $40k to $25k as supply normalises. A100 secondary market $9-10k.</span></div>
          <div className="flex gap-2"><TrendingUp size={12} className="text-rose shrink-0 mt-0.5" /><span><b className="text-rose">Server prices rising:</b> Dell/HPE rack servers up 25-30% due to tariffs, supply constraints, and AI demand.</span></div>
          <div className="flex gap-2"><TrendingDown size={12} className="text-emerald shrink-0 mt-0.5" /><span><b className="text-emerald">Cloud GPU falling:</b> CoreWeave/Lambda pushing prices down. A100 cloud now under $1/hr on spot.</span></div>
          <div className="flex gap-2"><Minus size={12} className="text-text-muted shrink-0 mt-0.5" /><span><b className="text-text-primary">Cloud compute flat:</b> Major hyperscalers haven't changed list prices significantly. Savings via reserved instances.</span></div>
          <div className="flex gap-2"><TrendingUp size={12} className="text-rose shrink-0 mt-0.5" /><span><b className="text-rose">Semiconductors up:</b> Leading-edge wafer costs (3nm) up 60% since 2021. Advanced packaging (CoWoS) up 68%.</span></div>
          <div className="flex gap-2"><TrendingDown size={12} className="text-emerald shrink-0 mt-0.5" /><span><b className="text-emerald">Memory stabilising:</b> DDR4 RDIMM halved since 2021. DDR5 mainstream. NVMe SSD steady decline.</span></div>
        </div>
      </div>

      <div className="card p-3">
        <p className="text-[9px] text-text-muted text-center">
          Sources: AWS/Azure/GCP public pricing pages, NVIDIA list prices, Dell/HPE configurators,
          TrendForce (DRAM), IC Insights (wafer costs). Quarterly averages. Some data estimated for upcoming quarters.
        </p>
      </div>
    </div>
  )
}
